import { Box, Typography } from "@mui/material";
import pluralize from "pluralize";

import { MergeImpact } from "./inventoryOverlaps";

export type MergeOutcome = {
  autoMergeCount: number;
  keptCount: number;
  combinedCount: number;
  blankCount: number;
};

type MergeImpactSummaryProps = {
  impact: MergeImpact;
  mode: "preview" | "outcome";
  outcome?: MergeOutcome;
};

function PreviewSummary({ impact }: { impact: MergeImpact }) {
  const inventoryCount = impact.decisionCount + impact.autoMergeCount;
  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
        This merge impacts:
      </Typography>
      <ul style={{ margin: "4px 0", paddingLeft: 24 }}>
        <li>
          <Typography variant="body2">
            {impact.entryCount} {pluralize("chemical record", impact.entryCount)}
          </Typography>
        </li>
        {impact.filedCount > 0 && (
          <li>
            <Typography variant="body2">
              {impact.filedCount} {pluralize("filed report", impact.filedCount)}
            </Typography>
          </li>
        )}
        {impact.openCount > 0 && (
          <li>
            <Typography variant="body2">
              {impact.openCount} {pluralize("open report", impact.openCount)}
            </Typography>
          </li>
        )}
        <li>
          <Typography variant="body2">
            {inventoryCount} {pluralize("chemical inventory", inventoryCount)}
          </Typography>
        </li>
      </ul>
    </div>
  );
}

function OutcomeSummary({ impact, outcome }: { impact: MergeImpact; outcome: MergeOutcome }) {
  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
        This merge impacted:
      </Typography>
      <ul style={{ margin: "4px 0", paddingLeft: 24 }}>
        <li>
          <Typography variant="body2">
            {impact.entryCount} {pluralize("chemical record", impact.entryCount)} merged into 1
          </Typography>
        </li>
        {outcome.autoMergeCount > 0 && (
          <li>
            <Typography variant="body2">
              {outcome.autoMergeCount} {pluralize("inventory", outcome.autoMergeCount)} merged
              automatically
            </Typography>
          </li>
        )}
        {outcome.keptCount > 0 && (
          <li>
            <Typography variant="body2">
              {outcome.keptCount} {pluralize("inventory", outcome.keptCount)} kept
            </Typography>
          </li>
        )}
        {outcome.combinedCount > 0 && (
          <li>
            <Typography variant="body2">
              {outcome.combinedCount} {pluralize("inventory", outcome.combinedCount)} combined
            </Typography>
          </li>
        )}
        {outcome.blankCount > 0 && (
          <li>
            <Typography variant="body2">
              {outcome.blankCount} {pluralize("inventory", outcome.blankCount)} left blank
            </Typography>
          </li>
        )}
      </ul>
    </div>
  );
}

/**
 * The merge impact map: how many catalog entries collapse into one, how many
 * inventory overlaps need an explicit decision (split reports filed/open),
 * and how many merge automatically. In "outcome" mode (after a successful
 * merge) it shows
 * what actually happened instead of what's about to.
 */
export function MergeImpactSummary(props: MergeImpactSummaryProps) {
  if (props.mode === "outcome") {
    return (
      <Box data-testid="merge-impact-summary">
        {props.outcome && <OutcomeSummary impact={props.impact} outcome={props.outcome} />}
      </Box>
    );
  }
  return (
    <Box data-testid="merge-impact-summary">
      <PreviewSummary impact={props.impact} />
    </Box>
  );
}
